/**
 * W46 — Account Health Dashboard.
 * Dot matrix, decay heatmap, in-flight CP coverage grid.
 * D-098: composite=0 → "unscored" gray dot.
 * No exports. No Recharts.
 */

import { useEffect, useState } from "react";
import { getSession } from "@/lib/session";
import { AccountDotMatrix, type AccountDot } from "@/components/charts/svg/AccountDotMatrix";
import { DecayHeatmap, type DecayAccount } from "@/components/charts/svg/DecayHeatmap";
import { scoreBadgeColor } from "@/lib/forecast-buckets";

interface AccountHealthItem {
  id: string; name: string; composite: number;
  scoreBand: "unscored" | "green" | "yellow" | "orange" | "red";
  bucket: string; dealValue: number; daysSince: number; status: string;
  cpCoverage: Record<string, boolean>;
}

const CP_KEYS = ["cp1","cp2","cp3","cp4","cp5","cp6","cp7","cp8","cp9","cp10"];

function fmt(v: number): string {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
}

export default function AccountHealth() {
  const session = getSession();
  const [data, setData] = useState<{ accounts: AccountHealthItem[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<"days" | "score" | "name">("days");

  useEffect(() => {
    const token = session?.token;
    if (!token) { setError("Not authenticated"); setLoading(false); return; }
    fetch("/api/dashboards/account-health", { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error(`${r.status}`); return r.json(); })
      .then((d) => { setData(d); setLoading(false); })
      .catch((e) => { console.error("[account-health]", e); setError("Could not load account health data"); setLoading(false); });
  }, [session?.token]);

  const dotAccounts: AccountDot[] = (data?.accounts ?? []).map((a) => ({
    id: a.id, name: a.name, composite: a.composite, scoreBand: a.scoreBand,
    bucket: a.bucket, dealValue: a.dealValue, daysSince: a.daysSince, status: a.status,
  }));

  const decayAccounts: DecayAccount[] = (data?.accounts ?? [])
    .filter((a) => a.status !== "Won" && a.status !== "Lost")
    .map((a) => ({ name: a.name, daysSince: a.daysSince, dealValue: a.dealValue }));

  const inFlight = (data?.accounts ?? [])
    .filter((a) => a.status !== "Won" && a.status !== "Lost")
    .sort((a, b) => sortBy === "score" ? a.composite - b.composite
      : sortBy === "days" ? b.daysSince - a.daysSince
      : a.name.localeCompare(b.name));

  return (
    <div className="flex flex-col gap-6">
      <h2 className="text-[15px] font-semibold text-foreground">Account Health</h2>
      {error && <p className="text-[12px] text-[#DC2626]">{error}</p>}

      <div className="bg-[hsl(var(--surface))] border border-border rounded-lg p-4">
        <h3 className="text-[12px] font-semibold text-muted-foreground uppercase tracking-wide mb-3">
          Composite Score Distribution
        </h3>
        <p className="text-[11px] text-muted-foreground mb-3">
          One dot per account. Gray = not yet scored (scoring in progress).
        </p>
        {loading ? <div className="h-32 w-full bg-[hsl(var(--surface-2))] rounded animate-pulse" />
          : <AccountDotMatrix accounts={dotAccounts} />}
      </div>

      <div className="bg-[hsl(var(--surface))] border border-border rounded-lg p-4">
        <h3 className="text-[12px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">
          Decay Heatmap
        </h3>
        <p className="text-[11px] text-muted-foreground mb-3">
          Active accounts by days since last touch and deal value. Red intensity indicates concentration of at-risk accounts.
        </p>
        {loading ? <div className="h-32 w-full bg-[hsl(var(--surface-2))] rounded animate-pulse" />
          : <DecayHeatmap accounts={decayAccounts} />}
      </div>

      <div className="bg-[hsl(var(--surface))] border border-border rounded-lg p-4">
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <h3 className="text-[12px] font-semibold text-muted-foreground uppercase tracking-wide">
            In-Flight Checkpoint Coverage
          </h3>
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-muted-foreground">Sort:</span>
            {(["days","score","name"] as const).map((s) => (
              <button key={s} type="button" onClick={() => setSortBy(s)}
                className={`text-[11px] px-2 py-0.5 rounded ${sortBy === s ? "bg-[#0F766E] text-white" : "text-muted-foreground hover:text-foreground"}`}>
                {s === "days" ? "Days" : s === "score" ? "Score" : "Name"}
              </button>
            ))}
          </div>
        </div>
        {loading ? <div className="h-40 w-full bg-[hsl(var(--surface-2))] rounded animate-pulse" />
          : inFlight.length === 0 ? <p className="text-[12px] text-muted-foreground">No active accounts</p>
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-[11px] border-collapse min-w-[640px]">
                <thead>
                  <tr>
                    <th className="text-left py-1.5 pr-3 font-medium text-muted-foreground sticky left-0 bg-[hsl(var(--surface))]">Account</th>
                    <th className="text-center px-1.5 py-1.5 font-medium text-muted-foreground">Score</th>
                    <th className="text-center px-1.5 py-1.5 font-medium text-muted-foreground">Bucket</th>
                    {CP_KEYS.map((cp) => (
                      <th key={cp} className="text-center px-1 py-1.5 font-medium text-muted-foreground">{cp.toUpperCase()}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {inFlight.map((acc) => (
                    <tr key={acc.id} className="border-t border-border/30 hover:bg-[hsl(var(--surface-2))]/40">
                      <td className="py-1.5 pr-3 font-medium text-foreground max-w-[140px] truncate sticky left-0 bg-[hsl(var(--surface))]">
                        {acc.name}
                      </td>
                      <td className="text-center px-1.5 py-1.5 tabular-nums">
                        {acc.composite === 0
                          ? <span className="text-[#6B7280] italic text-[10px]">\u2014</span>
                          : <span className="font-semibold" style={{ color: scoreBadgeColor(acc.composite) }}>{acc.composite}</span>}
                      </td>
                      <td className="text-center px-1.5 py-1.5">
                        <span className="text-[10px] font-semibold whitespace-nowrap text-muted-foreground">
                          {acc.bucket}
                        </span>
                      </td>
                      {CP_KEYS.map((cp) => (
                        <td key={cp} className="text-center px-1 py-1.5">
                          <span className="inline-block w-4 h-4 rounded-sm"
                            title={acc.cpCoverage[cp] ? `${cp.toUpperCase()} answered` : `${cp.toUpperCase()} open`}
                            style={{ backgroundColor: acc.cpCoverage[cp] ? "#16A34A" : "#1F2937", border: "1px solid #374151" }} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>
    </div>
  );
}
